import { toast } from "react-toastify";
import { useImpersonation } from "../../../store/impersonation/useImpersonation";
import {
  getImpersonationSession,
  getSessionHomePath,
  isAdminSession,
  isImpersonating,
  restoreOriginalSession,
  startImpersonationSession,
} from "../../auth";

const getItemId = (item) => item?.id || item?._id || item?.storeId || item?.employeeId || item?.userId;

const getCreatedAt = (user) => {
  const timestamp = user.createdAt ? Date.parse(user.createdAt) : NaN;
  return Number.isFinite(timestamp) ? timestamp : Infinity;
};

const compareBranchUsers = (left, right) => {
  const leftDate = getCreatedAt(left);
  const rightDate = getCreatedAt(right);
  if (leftDate !== rightDate) return leftDate < rightDate ? -1 : 1;

  const leftId = String(getItemId(left));
  const rightId = String(getItemId(right));
  return leftId < rightId ? -1 : leftId > rightId ? 1 : 0;
};

export class ImpersonationController {
  constructor(props) {
    this.model = props.models.impersonation;
    this.actions = useImpersonation.getState().actions;
    this.flowVersion = 0;
  }

  cancelPending = () => {
    this.flowVersion += 1;
    this.actions.setAutomaticStarting(false);
    this.actions.setLoading(false);
    this.actions.setStarting(false);
  };

  restartAutomatic = async (returnPath, isActive = () => true) => {
    const canRestoreAdmin = () => isImpersonating() && getImpersonationSession()?.originalSession?.type === "admin";
    if (!isActive() || !canRestoreAdmin() || useImpersonation.getState().data.automaticStarting) return;

    const version = this.flowVersion;
    this.actions.setAutomaticStarting(true);

    try {
      await this.model.stop();
      if (version !== this.flowVersion || !isActive() || !canRestoreAdmin()) return;

      if (!restoreOriginalSession()) {
        toast.error("Não foi possível recuperar a conta administrativa. A troca de loja foi cancelada.", { theme: "colored" });
        window.location = getSessionHomePath();
        return false;
      }

      // Recarrega o contexto administrativo antes de consultar e acessar a nova loja.
      window.location = returnPath;
    } finally {
      if (version === this.flowVersion) this.actions.setAutomaticStarting(false);
    }
  };

  startAutomatic = async (storeId, isActive = () => true) => {
    if (!isAdminSession() || isImpersonating() || useImpersonation.getState().data.automaticStarting) return;

    const version = this.flowVersion;
    const isCurrent = () => version === this.flowVersion && isActive() && isAdminSession() && !isImpersonating();
    this.actions.setAutomaticStarting(true);

    try {
      const stores = await this.loadStores(isCurrent);
      if (!isCurrent()) return;
      if (!stores) return false;

      if (!/^[a-f\d]{24}$/i.test(storeId)) {
        return false;
      }

      const store = stores.find((item) => String(getItemId(item)).toLowerCase() === storeId.toLowerCase());
      if (!store) {
        return false;
      }

      const users = await this.selectStore(store, isCurrent);
      if (!isCurrent()) return;
      if (!users) return false;

      const user = users
        .filter((item) => item.type === "branch" && item.active === true)
        .sort(compareBranchUsers)[0];

      if (!user) {
        return false;
      }

      this.actions.selectUser(user);
      return await this.startSelectedSession(isCurrent);
    } finally {
      if (version === this.flowVersion) this.actions.setAutomaticStarting(false);
    }
  };

  open = async () => {
    this.actions.setOpen(true);
    await this.loadStores();
  };

  close = () => this.actions.setOpen(false);

  loadStores = async (isActive = () => true) => {
    const version = this.flowVersion;
    this.actions.setLoading(true);
    const stores = await this.model.getStores();
    if (version !== this.flowVersion || !isActive()) return;
    if (stores) this.actions.setStores(stores);

    this.actions.setLoading(false);
    return stores;
  };

  selectStore = async (store, isActive = () => true) => {
    const version = this.flowVersion;
    const storeId = getItemId(store);

    this.actions.selectStore(store);

    if (!storeId) return;

    this.actions.setLoading(true);
    const users = await this.model.getUsersByStore(storeId);
    if (version !== this.flowVersion || !isActive()) return;

    if (users) this.actions.setUsers(users);

    this.actions.setLoading(false);
    return users;
  };

  setStoreFilter = (value) => this.actions.setStoreFilter(value);

  setUserFilter = (value) => this.actions.setUserFilter(value);

  selectUser = (user) => this.actions.selectUser(user);

  start = () => {
    if (useImpersonation.getState().data.automaticStarting) return;
    return this.startSelectedSession();
  };

  startSelectedSession = async (isActive = () => true) => {
    if (!isActive() || useImpersonation.getState().data.starting) return;
    const version = this.flowVersion;
    const { selectedStore, selectedUser } = useImpersonation.getState().data;
    const storeId = getItemId(selectedStore);
    const userId = getItemId(selectedUser);

    if (!storeId || !userId) {
      toast.error("Selecione uma loja e um usuário para continuar.", { theme: "colored" });
      return;
    }

    this.actions.setStarting(true);
    const data = await this.model.start({ storeId, userId });
    if (version !== this.flowVersion || !isActive()) return;
    this.actions.setStarting(false);

    if (!data?.token) {
      if (data) toast.error("Não foi possível iniciar o acesso como usuário.", { theme: "colored" });
      return false;
    }

    startImpersonationSession(data);
    window.location = getSessionHomePath();
    return true;
  };

  stop = async () => {
    await this.model.stop();

    if (!restoreOriginalSession()) {
      toast.error("Não foi possível restaurar a sessão original. Faça login novamente.", { theme: "colored" });
      window.location = "/";
      return;
    }

    window.location = getSessionHomePath();
  };
}
